import { useEffect, useState } from 'react';
import apiClient from '../../clients/apiClient';
import Spinner from './Spinner';
import type { FollowUpPreview } from '../../hooks/useFollowUpQueue';

interface FollowUpPreviewModalProps {
  open: boolean;
  outreachId: string | null;
  onClose: () => void;
  onError?: (message: string) => void;
  onSent: (outreachId: string, recruiterName: string, company: string) => void;
  onSkipped: (outreachId: string, recruiterName: string, company: string) => void;
}

export default function FollowUpPreviewModal({
  open,
  outreachId,
  onClose,
  onError,
  onSent,
  onSkipped,
}: FollowUpPreviewModalProps) {
  const [preview, setPreview] = useState<FollowUpPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !outreachId) {
      setPreview(null);
      setError(null);
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    apiClient
      .get<FollowUpPreview>(`/api/follow-ups/${outreachId}/preview`)
      .then((res) => {
        if (isMounted) {
          setPreview(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          const detail = err?.response?.data?.detail || 'Failed to generate follow-up preview';
          const statusCode = err?.response?.status;
          if (statusCode === 404 || statusCode === 403) {
            onError?.(typeof detail === 'string' ? detail : 'Follow-up not found');
            onClose();
            return;
          }
          setError(typeof detail === 'string' ? detail : 'Failed to generate follow-up preview');
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [open, outreachId, onClose, onError]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending && !skipping) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, sending, skipping, onClose]);

  if (!open) return null;

  const handleApprove = async () => {
    if (!outreachId || sending) return;
    setSending(true);
    try {
      await apiClient.post(`/api/follow-ups/${outreachId}/approve`);
      onSent(outreachId, preview?.recruiter_name || 'Recruiter', preview?.company || '');
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to send follow-up';
      if (err?.response?.status === 404 || err?.response?.status === 403) {
        onError?.(typeof detail === 'string' ? detail : 'Follow-up not found');
        onClose();
        return;
      }
      setError(typeof detail === 'string' ? detail : 'Failed to send follow-up');
    } finally {
      setSending(false);
    }
  };

  const handleSkip = async () => {
    if (!outreachId || skipping) return;
    setSkipping(true);
    try {
      await apiClient.post(`/api/follow-ups/${outreachId}/skip`);
      onSkipped(outreachId, preview?.recruiter_name || 'Recruiter', preview?.company || '');
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to skip follow-up';
      if (err?.response?.status === 404 || err?.response?.status === 403) {
        onError?.(typeof detail === 'string' ? detail : 'Follow-up not found');
        onClose();
        return;
      }
      setError(typeof detail === 'string' ? detail : 'Failed to skip follow-up');
    } finally {
      setSkipping(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px] transition-opacity"
        onClick={() => {
          if (!sending && !skipping) onClose();
        }}
      />

      {/* Modal Card */}
      <div
        className="relative bg-white rounded-3xl p-6 sm:p-7 max-w-xl w-full shadow-2xl border border-gray-100 z-10 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-2xl bg-amber-50 border border-amber-200/60 flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-bold text-gray-900 leading-tight">
                  Follow-up Confirmation
                </h3>
                {preview && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                    #{preview.follow_up_number}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Review the personalized email before sending to your contact
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={sending || skipping}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto py-4 space-y-4 pr-1">
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center text-center">
              <Spinner />
              <p className="text-sm font-medium text-gray-700 mt-3">Drafting follow-up preview...</p>
              <p className="text-xs text-gray-400 mt-1">Applying humanized tone and strict resume grounding</p>
            </div>
          ) : error ? (
            <div className="p-5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 text-sm">
              <div className="flex items-center gap-2 font-semibold text-rose-800 mb-1">
                <svg className="w-5 h-5 text-rose-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>{error.toLowerCase().includes('access denied') ? 'Unauthorized Access' : 'Could Not Load Preview'}</span>
              </div>
              <p className="text-xs text-rose-700 leading-relaxed">{error}</p>
              {!error.toLowerCase().includes('access denied') && (
                <button
                  onClick={() => {
                    if (outreachId) {
                      setLoading(true);
                      setError(null);
                      apiClient
                        .get<FollowUpPreview>(`/api/follow-ups/${outreachId}/preview`)
                        .then((res) => {
                          setPreview(res.data);
                          setLoading(false);
                        })
                        .catch((err) => {
                          const detail = err?.response?.data?.detail || 'Failed to retry preview';
                          setError(typeof detail === 'string' ? detail : 'Failed to retry preview');
                          setLoading(false);
                        });
                    }
                  }}
                  className="mt-3 px-3.5 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 transition-colors"
                >
                  Retry
                </button>
              )}
            </div>
          ) : preview ? (
            <>
              {/* Recipient context bar */}
              <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5 bg-gray-50 border border-gray-100 rounded-2xl text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">{preview.recruiter_name}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-600">{preview.company}</span>
                  {preview.role && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-500 italic">{preview.role}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Subject Line */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                  Subject
                </label>
                <div className="w-full px-3.5 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-900 select-all">
                  {preview.subject}
                </div>
              </div>

              {/* Email Body preview box */}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                  Email Message
                </label>
                <div className="w-full p-4 bg-[#faf9f6] border border-amber-900/10 rounded-2xl text-[13.5px] leading-relaxed text-gray-800 whitespace-pre-wrap font-sans select-all shadow-inner">
                  {preview.body}
                </div>
              </div>

              {/* Delivery mode note */}
              <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-amber-50/70 border border-amber-200/60 text-xs text-amber-900">
                <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>
                  <strong>Confirmation Required:</strong> Follow-ups will never send without your explicit approval unless you enable auto-send in Settings.
                </span>
              </div>
            </>
          ) : null}
        </div>

        {/* Footer Actions */}
        <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-2">
          {error ? (
            <div className="flex items-center justify-end w-full">
              <button
                onClick={onClose}
                className="px-5 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={handleSkip}
                disabled={loading || sending || skipping || !preview}
                className="px-3.5 py-2 text-xs font-semibold text-gray-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl border border-gray-200 transition-colors disabled:opacity-50"
                title="Permanently skip this follow-up cycle"
              >
                {skipping ? <Spinner /> : 'Skip Cycle'}
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  disabled={sending || skipping}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={loading || !preview || sending || skipping}
                  className="flex items-center gap-2 px-5 py-2 text-xs font-semibold text-white rounded-xl shadow-md transition-all disabled:opacity-50 cursor-pointer"
                  style={{ backgroundColor: 'var(--g, #059669)' }}
                >
                  {sending ? (
                    <>
                      <Spinner />
                      <span>Sending email...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                      <span>Confirm &amp; Send</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
